// Content for /for/schools — schools, colleges, universities, groups of schools and councils.
//
// TRACE: every `capability` below names the spec in openspec/specs/ that backs the claim. This
// is the audit trail for the "no claim without a shipped capability" rule (tasks 4.6). If a
// capability is ever removed, its bullet comes out of the copy with it.
//
// Written for the people who read it (2026-10-04 review): features are grouped by who uses
// them, terms like baseload and SECR are said in plain words, and the vocabulary works for a
// Scottish independent school as well as an English state one: pupils, bursars, governors,
// groups of schools and councils, not academy-trust shorthand.

export default {
    key: 'schools',
    eyebrow: 'For schools, colleges, groups of schools and councils',
    title: 'Make energy something your whole school plays with, learns from and saves',
    // Underlined on the playful (Joulelingo) look; must be a substring of `title`.
    titleHighlight: 'plays with',
    subtitle:
        'Your own meter readings, every half hour, turned into a weekly quiz, classroom activities, real datasets for lessons and a house competition, plus the targets, alerts and reports the bursar needs. For a group of schools, one view across every site.',
    heroChips: [
        'A weekly quiz on your own buildings',
        'Activities and real datasets for lessons',
        'House competitions',
        'Carbon reports for governors'
    ],

    // Whose problem each feature solves. The page renders one section per audience, in this
    // order; every feature names one of these keys.
    audiences: [
        {
            key: 'classroom',
            title: 'For pupils, teachers and the eco-committee',
            lede: 'Energy pupils can see, measure, puzzle over and compete on, using their own school’s numbers.'
        },
        {
            key: 'bursar',
            title: 'For the bursar and estates team',
            lede: 'Spend less, and stop paying for what nobody used.'
        },
        {
            key: 'leadership',
            title: 'For heads and governors',
            lede: 'Know where the school stands, and show it.'
        }
    ],

    features: [
        {
            audience: 'classroom',
            title: 'A weekly energy quiz about your own buildings',
            detail: 'Ten questions a week, the same for the whole school, one scored go each, on a leaderboard by pupil and by house. Every question comes from your own meters: which building used most, which day was busiest, how many kettles’ worth a building used. Pitched at ages 7–11, 11–14 or 14–16, with a question of the day and unlimited practice rounds in between. Pupils appear by first name and initial by default, never by email.',
            capability: 'energy-quiz / engagement-feedback'
        },
        {
            audience: 'classroom',
            title: 'Quiz tools for the teacher',
            detail: 'A class mode for the board, a printable worksheet with a separate answer key, and a view of which questions the class got wrong and the wrong answer most of them picked, with accuracy by skill (estimating, comparing, ratio, reading data).',
            capability: 'energy-quiz'
        },
        {
            audience: 'classroom',
            title: 'Ready-made classroom activities',
            detail: 'A library of pupil activities, tagged by key stage and subject, from a Baseload Hunt and a Meter Walkabout to a Vampire Power Hunt, plus practical actions for staff. Record what each class did with notes, photos and the house that earned it.',
            capability:
                'education-content-model / education-library-ui / education-recording'
        },
        {
            audience: 'classroom',
            title: 'Real data for real lessons',
            detail: 'Class Projects: ready-made datasets from your own meters with starter questions (a year in the life of the school, weekday against weekend, term against holidays, summer against winter), or build your own, downloadable as CSV or Excel for maths, science and geography.',
            capability: 'power-class-projects-ui / power-export-api'
        },
        {
            audience: 'classroom',
            title: 'A house competition that is fair',
            detail: 'Houses earn points for quiz scores, quiz turnout and activities they complete, by week, month, term or all time. Houses with their own meters are also ranked on how much they cut, not how big they are, so the small house can beat the big one.',
            capability: 'house-standings / pupil-groups / energy-quiz'
        },
        {
            audience: 'classroom',
            title: 'Numbers pupils can picture',
            detail: 'Yesterday’s electricity as kettles boiled, phones charged and miles an electric car could drive, drawn as a trip on a map starting at your school, plus what-if sliders for switching things off at lunchtime.',
            capability:
                'power-equivalents / ev-trip-visualization / power-what-if-sliders'
        },
        {
            audience: 'classroom',
            title: 'A team goal, a daily tip, streaks and badges',
            detail: 'On joulelingo.com pupils meet Jolt, who sets one goal for the whole school each week (5% less electricity than usual, with a recap on Mondays), gives a tip of the day from your own readings, and keeps each pupil’s streak of weekly challenges and their badges. Teachers see class totals only.',
            capability: 'pupil-home'
        },
        {
            audience: 'classroom',
            title: 'Your week on the hallway TV',
            detail: 'A hallway screen showing the week’s energy, the team goal, the house league and a tip, for the whole school or building by building in turn. Staff make a short link for each TV, typed with the remote as a five-character code, so nobody is signed in on it. No kWh, costs or names on screen.',
            capability: 'hallway-screen / house-standings'
        },
        {
            audience: 'bursar',
            title: 'Find the holidays you are paying for',
            detail: 'Every holiday and closure costed in £ and CO₂, by building or by meter, so the cost of heating and lighting an empty school stops being invisible.',
            capability: 'holiday-analytics / holiday-analysis-ui'
        },
        {
            audience: 'bursar',
            title: 'Your term dates, set up once',
            detail: 'Import your calendar from a file or a published link, and holidays, termly budgets and the academic-year carbon report all follow it.',
            capability:
                'term-calendar-import / sector-neutral-calendar / budget-model'
        },
        {
            audience: 'bursar',
            title: 'Alerts before the bill arrives',
            detail: 'A warning when the energy a building uses overnight, with nobody in it, starts creeping up, or when spending is heading over budget. Plus a weekly or monthly summary email to the people who can act.',
            capability:
                'baseload-creep-alert / budget-breach-alert / report-subscriptions'
        },
        {
            audience: 'bursar',
            title: 'Never miss a contract renewal window',
            detail: 'Record each contract’s end date and notice period, and get a reminder before the deadline, so the school is not rolled onto expensive out-of-contract rates. When renewal comes round, download a tender pack of your consumption for suppliers to quote on.',
            capability:
                'supply-contract-registry / contract-renewal-alerts / renewal-tender-pack'
        },
        {
            audience: 'bursar',
            title: 'Check every line of the bill before you pay it',
            detail: 'Each charge on a supplier’s bill checked against what the meters recorded and the rate in your contract, so a billing error is queried before it is paid. An alert flags a bill that disagrees with the meters by more than your tolerance.',
            capability: 'bill-statement-model / bill-discrepancy-alert'
        },
        {
            audience: 'bursar',
            title: 'Pupils, staff and houses from your MIS',
            detail: 'Connect iSAMS and pupils, staff and houses arrive automatically overnight, so nobody types in a class list.',
            capability:
                'mis-provider-registry / mis-user-import / mis-building-import'
        },
        {
            audience: 'leadership',
            title: 'Set a reduction target and watch it',
            detail: 'A target for each fuel, with progress tracked week by week against a starting point, so “use less energy” becomes a number everyone can see.',
            capability: 'target-model / target-progress / target-ui'
        },
        {
            audience: 'leadership',
            title: 'Compare against schools like yours',
            detail: 'Opt in to see how the school compares with similar schools, anonymously, alongside published figures for a typical school.',
            capability: 'anonymised-peer-benchmarking / static-benchmarks'
        },
        {
            audience: 'leadership',
            title: 'Carbon reports for governors and funders',
            detail: 'An annual carbon report in the shape the UK’s carbon reporting rules (SECR) ask for, using the government’s published conversion factors, as PDF or CSV.',
            capability:
                'carbon-report-generation / carbon-factor-tables / carbon-report-export'
        }
    ],

    asides: [
        {
            title: 'Groups of schools, trusts and councils',
            body: 'Group your schools and get a read-only view across all of them: totals, school-by-school comparison, and league tables adjusted for floor area and pupil numbers, so a large senior school and a small junior school can be compared fairly. Each school keeps its own account and its own data; the group view sits on top.',
            capability:
                'portfolio-dashboard / portfolio-rankings-ui / org groups (e9c0ad4)'
        },
        {
            title: 'Universities and colleges',
            body: 'The same platform, with the words changed to match: halls rather than houses, and a campus handled as a set of buildings with their own meters, tariffs and opening hours. Usage is adjusted for the weather, so a cold term can be compared fairly with a mild one.',
            capability:
                'sector-terminology / org-flavour / degree-day-computation'
        }
    ],

    // A real school, named with Ross's agreement (2026-10-04). Shown on the platform's own host
    // only: Fettes agreed to be named by us, not by a partner reselling under its own brand.
    reference: {
        quote: 'Fettes College in Edinburgh runs its energy monitoring on {product}.',
        platformHostOnly: true
    },

    proofPoints: [
        'Engagement pupils respond to: a weekly quiz on their own buildings, house competitions, real datasets and everyday comparisons, not just a chart of kilowatt-hours',
        'Finds the energy used overnight, at weekends and over the holidays, without anyone having to go looking',
        'Reports ready for governors and funders, without rebuilding a spreadsheet each year'
    ],

    ctaTitle: 'See it with your own meter data',
    ctaBody:
        'We connect to your existing electricity and gas suppliers, so a demo can show your own school’s readings rather than a sample school.'
}
